import {
	Box,
	Card,
	createStyles,
	Divider,
	Group,
	Modal,
	ScrollArea,
	Stack,
	UnstyledButton,
	useMantineTheme,
} from '@mantine/core';
import { type AiTaskOut, useApiSuggestedAiTasks } from '@repo/api-codegen';
import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import { Button, Icon, Text } from '@repo/foundations';
import { useState } from 'react';
import { ScrollableModal } from '../../components/ScrollableModal/ScrollableModal';
import type { PrefilledAgentData } from '../../features/AiChat/types';
import { AgentEditor } from './AgentEditor';
import { CustomAgentImage, TemplateAgentImage } from './Images';

const useTemplateStyles = createStyles((theme) => ({
	markdownDescription: {
		color: theme.other.getColor('text/secondary/default'),
		fontSize: theme.fontSizes.sm,

		// Override default markdown styles to match card styling
		'& p': {
			margin: 0,
		},
		'& p:not(:last-child)': {
			marginBottom: theme.spacing.xs,
		},
	},
	templateCard: {
		borderColor: theme.other.getColor('border/secondary/default'),
	},
	templateCardSelected: {
		borderColor: theme.other.getColor('border/primary/default'),
		backgroundColor: theme.other.getColor('fill/transparent/active'),
	},
}));

function CreateAgentStep1({
	onSelect,
}: {
	onSelect: (step: 'step1' | 'step2' | 'step3') => void;
}) {
	const theme = useMantineTheme();
	return (
		<>
			<Stack spacing="lg">
				<Text weight="semibold" size="sm" align="center">
					Select a way to get started
				</Text>
				<Group spacing="md" noWrap grow>
					<UnstyledButton onClick={() => onSelect('step3')}>
						<Card withBorder radius="md">
							<Stack spacing="sm">
								<CustomAgentImage />
								<Stack spacing="3xs">
									<Text size="sm" weight="bold">
										Custom agent
									</Text>
									<Text size="sm" color="text/secondary/default">
										Build an agent with complete flexibility
									</Text>
								</Stack>
							</Stack>
						</Card>
					</UnstyledButton>
					<UnstyledButton onClick={() => onSelect('step2')}>
						<Card withBorder radius="md">
							<Stack spacing="sm">
								<TemplateAgentImage />
								<Stack spacing="3xs">
									<Text size="sm" weight="bold">
										Template
									</Text>
									<Text size="sm" color="text/secondary/default">
										Save time with a pre-built template
									</Text>
								</Stack>
							</Stack>
						</Card>
					</UnstyledButton>
				</Group>
			</Stack>
			<Box
				mt="lg"
				style={{
					marginBottom: `calc(${theme.spacing.md} * -1)`,
					marginLeft: `calc(${theme.spacing.md} * -1)`,
					marginRight: `calc(${theme.spacing.md} * -1)`,
				}}
			>
				<Divider />
				<Group p="md">
					<Text size="sm">
						<a href="https://docs.secoda.com/features/ai-assistant/secoda-ai-agents">
							Learn more about Secoda AI agents
						</a>
					</Text>
				</Group>
			</Box>
		</>
	);
}

function CreateAgentStep2({
	selectedTemplate,
	onChangeSelectedTemplate,
}: {
	selectedTemplate: AiTaskOut | null;
	onChangeSelectedTemplate: (template: AiTaskOut) => void;
}) {
	const { classes } = useTemplateStyles();
	const { data: suggestions, isLoading } = useApiSuggestedAiTasks({});

	if (isLoading) {
		return (
			<Stack spacing="xs">
				{Array.from({ length: 6 }, (_, index) => (
					<Card key={`loading-skeleton-${index}`} withBorder radius="md">
						<Stack spacing="3xs">
							<Text size="sm" color="text/secondary/default">
								Loading suggestions...
							</Text>
						</Stack>
					</Card>
				))}
			</Stack>
		);
	}

	const aiTaskSuggestions = suggestions?.suggestions || [];

	return (
		<Stack spacing="md">
			<ScrollArea.Autosize mah={568}>
				{aiTaskSuggestions.map((suggestion) => {
					const template = {
						title: suggestion.title,
						prompt: suggestion.prompt,
						cron_expression: suggestion.cron_expression,
						web_search: suggestion.web_search,
					} as AiTaskOut;
					const isSelected = selectedTemplate?.title === template.title;
					return (
						<UnstyledButton
							key={template.title}
							onClick={() => onChangeSelectedTemplate(template)}
							aria-pressed={isSelected}
						>
							<Card
								withBorder
								radius="md"
								className={
									isSelected
										? classes.templateCardSelected
										: classes.templateCard
								}
							>
								<Stack spacing="3xs">
									<Text size="sm" weight="bold">
										{template.title}
									</Text>
									<Box>
										<MarkdownRenderer className={classes.markdownDescription}>
											{template.prompt ?? ''}
										</MarkdownRenderer>
									</Box>
								</Stack>
							</Card>
						</UnstyledButton>
					);
				})}
			</ScrollArea.Autosize>
		</Stack>
	);
}

export function CreateAgentModal({
	onClose,
	agent,
	prefilledData,
}: {
	onClose: () => void;
	agent?: AiTaskOut | null;
	prefilledData?: PrefilledAgentData;
}) {
	const [step, setStep] = useState<'step1' | 'step2' | 'step3'>(
		agent || prefilledData ? 'step3' : 'step1'
	);
	const [template, setTemplate] = useState<AiTaskOut | null>(
		prefilledData
			? ({
					title: prefilledData.title,
					prompt: prefilledData.prompt,
					cron_expression: prefilledData.cronExpression,
				} as AiTaskOut)
			: null
	);
	const [selectedTemplate, setSelectedTemplate] = useState<AiTaskOut | null>(
		null
	);

	if (step === 'step1') {
		return (
			<Modal opened onClose={onClose} title="Create agent" size={480}>
				<CreateAgentStep1 onSelect={(chosenStep) => setStep(chosenStep)} />
			</Modal>
		);
	}

	if (step === 'step2') {
		return (
			<ScrollableModal
				opened
				onClose={onClose}
				title={
					<UnstyledButton onClick={() => setStep('step1')}>
						<Group spacing="3xs" py="3xs">
							<Icon name="chevronLeft" />
							<Text size="md" weight="bold">
								Select agent template
							</Text>
						</Group>
					</UnstyledButton>
				}
				size={480}
				withTopPadding
				footer={
					<Group position="right" spacing="sm">
						<Button variant="default" onClick={() => setStep('step1')}>
							Cancel
						</Button>
						<Button
							variant="primary"
							disabled={!selectedTemplate}
							onClick={() => {
								if (selectedTemplate) {
									setTemplate(selectedTemplate);
									setStep('step3');
								}
							}}
						>
							Create agent
						</Button>
					</Group>
				}
			>
				<CreateAgentStep2
					selectedTemplate={selectedTemplate}
					onChangeSelectedTemplate={setSelectedTemplate}
				/>
			</ScrollableModal>
		);
	}

	return (
		<Modal
			opened
			onClose={onClose}
			title={agent ? 'Edit agent' : 'Create agent'}
			size={640}
		>
			<AgentEditor
				agent={agent || template}
				isTemplate={!!template}
				fromAgentMessage={!!prefilledData}
				onCancel={onClose}
			/>
		</Modal>
	);
}
